// src/components/BackButton.tsx
//
// Hierarchical Up button used across all pages; Android/browser Back owns visit history.
// Standard style is "← {label}"
// (icon + text) for clarity on mobile and accessibility. Older pages used
// inconsistent styles (some icon-only, some icon+text); this component is the
// single source of truth — change the styling here to update every page.
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { parentPath, safeReturnTo } from '@/lib/appNavigation';

interface Props {
  /** Explicit logical parent; never a visit-history or returnTo destination. */
  to?: string;
  /** Override the displayed label. Defaults to t('common.back'). */
  label?: string;
  /** Extra classes appended to the default button styling. */
  className?: string;
}

export function BackButton({ to, label, className }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const parent = safeReturnTo(to) ?? parentPath(location.pathname);
  const { t } = useTranslation();
  const text = label ?? t('common.back');
  // Up is an ordinary navigation; the system Back button can still traverse visits.
  return (
    <button
      type="button"
      onClick={() => navigate(parent)}
      aria-label={text}
      className={`inline-flex min-h-[44px] items-center whitespace-nowrap rounded-md border px-3 text-sm hover:bg-muted ${className ?? ''}`.trim()}
    >
      ← {text}
    </button>
  );
}

